<template>
  <yv-form-search
    :formSearch="formSearch"
    :inlineSearchAction="true"
    :enableExport="false"
    @search="search"
    ref="formSearch"
  >
    <template #search-actions="{ actions }">
      <div class="flex justify-between">
        <md-button
          native-type="submit"
          @click="actions.search"
          class="md-info md-round"
          >{{ $t("common.search") }}
        </md-button>
      </div>
    </template>
  </yv-form-search>
</template>

<script>
import YvFormSearch from "@/components/Base/YvFormSearch.vue";
import { service_paths } from "@/constants/Services";
import { initLoader, getId, Message } from "@/helpers/utils";
import { store } from "@/store";
import Role from "@/constants/Role";
import { mapGetters, mapState } from "vuex";
import { hasValidRole } from "@/helpers/role-helper";

export default {
  components: { YvFormSearch },
  data() {
    const userRole = store.getters["user/role"];
    const formSearch = this.generateFormSearchByRole(userRole);

    return {
      formSearch,
      bulkActions: [
        {
          content: "Print as PDF",
          action: () => {}
        },
        {
          content: "Print as PNG",
          action: () => {}
        }
      ]
    };
  },
  computed: {
    selectedStudentId() {
      return this.formSearch[0].value;
    },
    selectedStudent() {
      return this.formSearch[0].options.find(
        student => student.id === this.selectedStudentId
      );
    },
    programOptIdx() {
      return this.formSearch.length - 1;
    },
    ...mapGetters("user", {
      userRole: "role",
      userInfo: "info"
    }),
    ...mapState("switchAccountChild", ["currentChild"]),
    isDefaultUser() {
      return (
        hasValidRole(this.userRole, [Role.Admin, Role.Principal]) ||
        !hasValidRole(this.userRole, Object.values(Role))
      );
    }
  },
  watch: {
    selectedStudentId(newSelectedStudentId) {
      if (!hasValidRole(this.userRole, [Role.Student, Role.Family])) {
        !!newSelectedStudentId &&
          this.fetchStudentPrograms(newSelectedStudentId);
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    generateFormSearchByRole(role) {
      const baseFormSearch = [
        {
          label: this.$t("score_report.student.label_filter_by_program"),
          name: "program_id",
          value: "",
          type: "select",
          options: []
        }
      ];

      if (hasValidRole(role, [Role.Student, Role.Family])) {
        return baseFormSearch;
      }

      return [
        {
          label: this.$t("score_report.student.label_filter_by_student"),
          name: "student_id",
          value: "",
          displayValue: "",
          options: [],
          type: "autocomplete",
          fields: {
            text: "displayName",
            value: "id"
          },
          searchKey: "searchKey",
          refetchOptions: this.fetchStudentOpts,
          width: 30
        },
        ...baseFormSearch
      ];
    },
    search(queryParams) {
      const { program_id } = queryParams;
      const selectedProgram = this.getSeletedProgram(program_id);
      const selectedStudent = this.getSelectedStudent();

      if (!selectedStudent) {
        Message.error("Please choose a student");
        return;
      }

      if (!selectedProgram) {
        Message.error("Student have no corresponding program");
        return;
      }

      this.$emit("set-new-report-params", {
        selectedProgram,
        selectedStudent
      });
    },
    getSelectedStudent() {
      if (hasValidRole(this.userRole, Role.Student)) {
        return {
          ...this.userInfo,
          id: getId(this.userInfo)
        };
      }

      if (hasValidRole(this.userRole, Role.Family)) {
        return { ...this.currentChild, id: getId(this.currentChild) };
      }

      return this.selectedStudent;
    },
    setNewStudentOptions(options) {
      this.formSearch[0].options = options;
    },
    fetchStudentOpts(queryObj) {
      const path = this.generateStudentAPIPath();
      queryObj = this.reGenQueryParamsByRole(queryObj);
      return this.$axios
        .get(path, queryObj, undefined, undefined, undefined, undefined, false)
        .then(response => {
          const newStudentOptions = response.data.data.map(student => ({
            ...student,
            id: getId(student),
            displayName: `${student.full_name} - ${student.student_code}`
          }));

          this.setNewStudentOptions(newStudentOptions);
        });
    },
    async fetchStudentPrograms(studentId) {
      return this.$axios
        .get(service_paths.programs, {
          user_id: studentId,
          limit: 100
        })
        .then(response => {
          const programs = response.data.data.map(program => ({
            ...program,
            id: getId(program),
            name: program.name
          }));

          this.formSearch[this.programOptIdx].options = programs;
          this.formSearch[this.programOptIdx].value = programs[0]?.id;

          return programs[0]?.id;
        });
    },
    getSeletedProgram(programId) {
      return this.formSearch[this.programOptIdx].options.find(
        program => program.id === programId
      );
    },
    reGenQueryParamsByRole(queryParams) {
      if (!queryParams) return null;

      if (this.isDefaultUser) {
        const { searchKey, ...restParams } = queryParams;
        return {
          ...restParams,
          search_key: searchKey,
          fields: ["full_name", "student_code"]
        };
      }

      return queryParams;
    },
    generateStudentAPIPath() {
      if (this.isDefaultUser) {
        return `${service_paths.students}/filter`;
      }

      return service_paths.students;
    },

    setUpDefaultParams() {
      const student = this.getSelectedStudent();
      const loader = initLoader();

      this.fetchStudentPrograms(getId(student))
        .finally(() => {
          loader.hide();
        })
        .then(programId =>
          this.search({
            program_id: programId
          })
        );
    },

    initialize() {
      if (hasValidRole(this.userRole, [Role.Student, Role.Family])) {
        this.setUpDefaultParams();
        return;
      }

      const loader = initLoader();
      this.fetchStudentOpts().finally(() => {
        loader.hide();
      });
    }
  }
};
</script>

<style lang="scss" scoped></style>
